import { Box, Text } from '@color/continuum';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { useCallback, useRef, useState } from 'react';

interface FileDropzoneProps {
  files: File[];
  onFilesChange: (files: File[]) => void;
  acceptedExtensions?: string;
  multiple?: boolean;
}

const renderLabel = (files: File[]) => {
  if (files.length === 0) {
    return null;
  }
  if (files.length === 1) {
    return files[0]?.name;
  }
  return `${files.length} files selected`;
};

export const FileDropzone = ({
  files,
  onFilesChange,
  acceptedExtensions,
  multiple = false,
}: FileDropzoneProps) => {
  const [isDragActive, setIsDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragActive(false);
  }, []);

  const acceptFiles = useCallback(
    (incoming: FileList | File[]) => {
      const list = Array.from(incoming);
      if (list.length === 0) return;
      onFilesChange(multiple ? list : list.slice(0, 1));
    },
    [multiple, onFilesChange]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragActive(false);
      acceptFiles(e.dataTransfer.files);
    },
    [acceptFiles]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) acceptFiles(e.target.files);
    },
    [acceptFiles]
  );

  const selectionLabel = renderLabel(files);

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label="File upload dropzone"
      onClick={() => inputRef.current?.click()}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
      }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 1,
        minHeight: '120px',
        cursor: 'pointer',
        backgroundColor: theme.palette.background.lightBg,
        borderRadius: 1,
        border: '2px dashed',
        borderColor: isDragActive
          ? theme.palette.primary.main
          : theme.palette.background.cardBorder,
        userSelect: 'none',
        transition: 'border-color 0.2s',
      })}
    >
      <input
        ref={inputRef}
        data-testid="file-input"
        type="file"
        accept={acceptedExtensions}
        multiple={multiple}
        aria-label={multiple ? 'Select files to upload' : 'Select file to upload'}
        style={{ display: 'none' }}
        onChange={handleInputChange}
      />
      <CloudUploadOutlinedIcon color={isDragActive ? 'primary' : 'action'} />
      {isDragActive ? (
        <Text variant="body2">
          {multiple ? 'Drop your files here to upload them' : 'Drop your file here to upload it'}
        </Text>
      ) : (
        <Text variant="body2" sx={{ textAlign: 'center' }}>
          {selectionLabel !== null ? (
            selectionLabel
          ) : (
            <>
              <Box component="span" sx={(theme) => ({ color: theme.palette.primary.main })}>
                {multiple ? 'Choose files' : 'Choose a file'}
              </Box>{' '}
              {multiple ? 'or drag them here to upload' : 'or drag it here to upload'}
            </>
          )}
        </Text>
      )}
    </Box>
  );
};
